import {
    defaultBaseQuery,
    transformErrorResponse,
    transformSuccessResponse,
} from "@/services";
import { createApi } from "@reduxjs/toolkit/query/react";

const adminDashboardAPI = createApi({
    reducerPath: "adminDashboardAPI",
    baseQuery: defaultBaseQuery,
    tagTypes: ["AdminDashboardSummary", "AdminAttendanceCounts", "AdminAttendanceSummary", "StudentAttendanceDashboard"],
    endpoints: (builder) => ({
        getAdminDashboardSummary: builder.query({
            query: () => ({
                url: `/dashboard/admin`,
                method: "GET",
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
            providesTags: ["AdminDashboardSummary"],
        }),
        getAdminAttendanceCounts: builder.query({
            query: ({ encryptedDepartmentId = "", encryptedShiftId = "", date = "" } = {}) => ({
                url: `/dashboard/admin-attendance/counts`,
                method: "GET",
                params: {
                    ...(encryptedDepartmentId && { encryptedDepartmentId }),
                    ...(encryptedShiftId && { encryptedShiftId }),
                    ...(date && { date }),
                },
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
            providesTags: ["AdminAttendanceCounts"],
        }),
        getAdminAttendanceSummary: builder.query({
            query: ({ date = "", encryptedDepartmentId = "", encryptedShiftId = "" } = {}) => ({
                url: `/dashboard/admin-attendance/summary`,
                method: "GET",
                params: {
                    ...(date && { date }),
                    ...(encryptedDepartmentId && { encryptedDepartmentId }),
                    ...(encryptedShiftId && { encryptedShiftId }),
                },
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
            providesTags: ["AdminAttendanceSummary"],
        }),
        getAdminAttendanceTrends: builder.query({
            query: ({ date = "", days = 7, encryptedDepartmentId = "", encryptedShiftId = "" } = {}) => ({
                url: `/dashboard/admin-attendance/trends`,
                method: "GET",
                params: {
                    ...(date && { date }),
                    ...(days && { days }),
                    ...(encryptedDepartmentId && { encryptedDepartmentId }),
                    ...(encryptedShiftId && { encryptedShiftId }),
                },
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
            providesTags: ["AdminAttendanceTrends"],
        }),
        getDepartmentPersonsAttendanceReport: builder.query({
            query: ({ departmentId = "", date = "", designationId = "", searchText = "" } = {}) => ({
                url: `/attendance-report/departments/persons`,
                method: "GET",
                params: {
                    ...(departmentId && { departmentId }),
                    ...(date && { date }),
                    ...(designationId && { designationId }),
                    ...(searchText && { searchText }),
                },
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
            providesTags: ["DepartmentPersonsAttendanceReport"],
        }),
        getStudentAttendanceDashboard: builder.query({
            query: ({ days = 7, date = "", programId = "", courseId = "", semesterYearId = "", admissionSystem = "", sortBy = "", sortDir = "" } = {}) => ({
                url: `/s-lams/dashboard`,
                method: "GET",
                params: {
                    days,
                    ...(date && { date }),
                    ...(programId && { programId }),
                    ...(courseId && { courseId }),
                    ...(semesterYearId && { semesterYearId }),
                    ...(admissionSystem !== undefined && admissionSystem !== "" && { admissionSystem }),
                    ...(sortBy && { sortBy, sortDir: sortDir ?? "" }),
                },
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
            providesTags: ["StudentAttendanceDashboard"],
        }),
    }),
});

export const {
    useGetAdminDashboardSummaryQuery,
    useLazyGetAdminDashboardSummaryQuery,
    useGetAdminAttendanceCountsQuery,
    useLazyGetAdminAttendanceCountsQuery,
    useGetAdminAttendanceSummaryQuery,
    useLazyGetAdminAttendanceSummaryQuery,
    useGetAdminAttendanceTrendsQuery,
    useLazyGetAdminAttendanceTrendsQuery,
    useGetDepartmentPersonsAttendanceReportQuery,
    useLazyGetDepartmentPersonsAttendanceReportQuery,
    useGetStudentAttendanceDashboardQuery,
    useLazyGetStudentAttendanceDashboardQuery,
} = adminDashboardAPI;

export default adminDashboardAPI;
